const CAPTURE_SIZE = 1_800;
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const DOWNLOAD_URL_LIFETIME_MS = 60_000;

const CAPTURE_PROPERTIES = [
  'display',
  'fill',
  'stroke',
  'stroke-width',
  'paint-order',
  'font-family',
  'font-size',
  'font-weight',
  'text-anchor',
  'dominant-baseline',
] as const;

/** A downloaded SVG has no page stylesheet, so freeze the visible marker styles. */
export function copyAiCandidateCaptureStyles(source: SVGSVGElement, copy: SVGSVGElement): void {
  copyCaptureStyles(source, copy, '.ai-candidate, .ai-candidate *');
}

/** Keep the current board material, coordinates and labels in standalone exports. */
export function copyBoardCaptureStyles(source: SVGSVGElement, copy: SVGSVGElement): void {
  copyCaptureStyles(
    source,
    copy,
    '.board-lines line, .star-point, .board-coordinate, .stone-move-label, .last-move-mark',
  );
}

function copyCaptureStyles(source: SVGSVGElement, copy: SVGSVGElement, selector: string): void {
  const originals = source.querySelectorAll<SVGElement>(selector);
  const targets = copy.querySelectorAll<SVGElement>(selector);
  originals.forEach((original, index) => {
    const target = targets[index];
    if (target === undefined) return;
    const style = getComputedStyle(original);
    for (const property of CAPTURE_PROPERTIES) {
      target.style.setProperty(property, style.getPropertyValue(property));
    }
  });
}

async function captureAssetDataUrl(href: string): Promise<string> {
  const response = await fetch(new URL(href, document.baseURI), { credentials: 'same-origin' });
  if (!response.ok) throw new Error('棋盘素材读取失败，请重试截图。');
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('棋盘素材读取失败，请重试截图。'));
    reader.readAsDataURL(blob);
  });
}

/** Embed full-resolution assets once per geometry, not once per stone. */
export async function embedBoardCaptureImages(copy: SVGSVGElement): Promise<void> {
  const assets = new Map<string, Promise<string>>();
  const groups = new Map<string, SVGImageElement[]>();
  for (const image of copy.querySelectorAll<SVGImageElement>('image')) {
    const href = image.getAttribute('href');
    if (href === null) continue;
    // A referenced image retains its own dimensions; <use> supplies each instance's
    // position and presentation. Keep different sizes/aspect modes as separate assets.
    const key = JSON.stringify([
      href,
      image.getAttribute('width'),
      image.getAttribute('height'),
      image.getAttribute('preserveAspectRatio'),
    ]);
    const group = groups.get(key) ?? [];
    group.push(image);
    groups.set(key, group);
    if (!assets.has(href)) {
      assets.set(
        href,
        href.startsWith('data:') ? Promise.resolve(href) : captureAssetDataUrl(href),
      );
    }
  }
  const ids = new Set(Array.from(copy.querySelectorAll('[id]'), (node) => node.id));
  const definitions = document.createElementNS(SVG_NAMESPACE, 'defs');
  let nextId = 0;
  await Promise.all(
    Array.from(groups.values()).map(async (images) => {
      const first = images[0]!;
      const dataUrl = await assets.get(first.getAttribute('href')!)!;
      if (images.length === 1) {
        first.setAttribute('href', dataUrl);
        return;
      }
      let id: string;
      do {
        id = `board-capture-image-${nextId++}`;
      } while (ids.has(id));
      ids.add(id);
      const asset = document.createElementNS(SVG_NAMESPACE, 'image');
      asset.setAttribute('id', id);
      asset.setAttribute('href', dataUrl);
      for (const name of ['width', 'height', 'preserveAspectRatio']) {
        const value = first.getAttribute(name);
        if (value !== null) asset.setAttribute(name, value);
      }
      definitions.append(asset);
      for (const image of images) {
        const instance = document.createElementNS(SVG_NAMESPACE, 'use');
        for (const attribute of Array.from(image.attributes)) {
          if (attribute.localName === 'href') continue;
          instance.setAttributeNS(attribute.namespaceURI, attribute.name, attribute.value);
        }
        instance.setAttribute('href', `#${id}`);
        image.replaceWith(instance);
      }
    }),
  );
  if (definitions.childElementCount > 0) copy.prepend(definitions);
}

function safePart(value: string): string {
  return value.replace(/[^A-Za-z0-9_-]/gu, '_').slice(0, 80) || 'fox-kifu';
}

export function foxBoardCaptureFilename(recordId: string, moveNumber: number): string {
  return `${safePart(recordId)}-move-${Math.max(0, Math.trunc(moveNumber))}.jpg`;
}

/** Preserve hand-drawn board overlays without modifying the visible board or exporting UI. */
export function appendBoardCaptureAnnotations(
  copy: SVGSVGElement,
  annotations: SVGSVGElement | null | undefined,
): void {
  if (annotations === null || annotations === undefined) return;
  const layer = annotations.cloneNode(true) as SVGSVGElement;
  layer.setAttribute('x', '0');
  layer.setAttribute('y', '0');
  layer.setAttribute('width', '100%');
  layer.setAttribute('height', '100%');
  copyCaptureStyles(annotations, layer, '.review-point-annotation');
  copy.append(layer);
}

export async function renderBoardJpeg(
  svg: SVGSVGElement,
  annotations?: SVGSVGElement | null,
): Promise<Blob> {
  const copy = svg.cloneNode(true) as SVGSVGElement;
  copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  copy.setAttribute('width', String(CAPTURE_SIZE));
  copy.setAttribute('height', String(CAPTURE_SIZE));
  copyAiCandidateCaptureStyles(svg, copy);
  copyBoardCaptureStyles(svg, copy);
  appendBoardCaptureAnnotations(copy, annotations);

  const shell = svg.closest('.board-wood-surface, .board-shell');
  const material = shell === null ? null : getComputedStyle(shell);
  const backgroundColor = material?.backgroundColor || '#d4a657';

  const background = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  background.setAttribute('x', '0');
  background.setAttribute('y', '0');
  background.setAttribute('width', '1000');
  background.setAttribute('height', '1000');
  background.setAttribute('rx', '10');
  background.setAttribute('fill', backgroundColor);
  const definitions = copy.querySelector('defs');
  definitions?.after(background);

  if (shell !== null) {
    const texture = getComputedStyle(shell, '::before');
    const href = /^url\(["']?(.*?)["']?\)$/u.exec(texture.backgroundImage)?.[1];
    if (href !== undefined) {
      const wood = document.createElementNS('http://www.w3.org/2000/svg', 'image');
      wood.setAttribute('href', href);
      wood.setAttribute('width', '1000');
      wood.setAttribute('height', '1000');
      wood.setAttribute('preserveAspectRatio', 'xMidYMid slice');
      wood.setAttribute('opacity', texture.opacity);
      background.after(wood);
    }
  }
  await embedBoardCaptureImages(copy);

  const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
  style.textContent = `
    .board-lines line{stroke:rgba(47,30,11,.84);stroke-width:2.3}
    .star-point{fill:#2f1e0b}
    .last-move-mark{fill:none;stroke:#d85838;stroke-width:7}
    .stone-move-label{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-weight:800;font-size:28px;paint-order:stroke;stroke-width:2px}
    .stone-move-label--black{fill:#fff;stroke:rgba(0,0,0,.3)}
    .stone-move-label--white{fill:#14251f;stroke:rgba(255,255,255,.6)}
    .board-hit{display:none}
  `;
  copy.prepend(style);

  // img-src allows blob:. Avoid another percent-encoded copy of the embedded assets.
  const serialized = new XMLSerializer().serializeToString(copy);
  const sourceUrl = URL.createObjectURL(new Blob([serialized], { type: 'image/svg+xml' }));
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const candidate = new Image();
      candidate.onload = () => resolve(candidate);
      candidate.onerror = () => reject(new Error('棋盘图像生成失败。'));
      candidate.src = sourceUrl;
    });
    const canvas = document.createElement('canvas');
    canvas.width = CAPTURE_SIZE;
    canvas.height = CAPTURE_SIZE;
    const context = canvas.getContext('2d');
    if (context === null) throw new Error('当前浏览器不支持棋盘截屏。');
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, CAPTURE_SIZE, CAPTURE_SIZE);
    context.drawImage(image, 0, 0, CAPTURE_SIZE, CAPTURE_SIZE);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (value) => (value === null ? reject(new Error('JPG 图像生成失败。')) : resolve(value)),
        'image/jpeg',
        0.94,
      );
    });
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
}

export async function downloadBoardJpeg(
  svg: SVGSVGElement,
  filename: string,
  annotations?: SVGSVGElement | null,
): Promise<void> {
  const jpeg = await renderBoardJpeg(svg, annotations);
  const downloadUrl = URL.createObjectURL(jpeg);
  const anchor = document.createElement('a');
  try {
    anchor.href = downloadUrl;
    anchor.download = filename.toLowerCase().endsWith('.jpg') ? filename : `${filename}.jpg`;
    document.body.append(anchor);
    anchor.click();
  } finally {
    anchor.remove();
    // Mobile browsers may consume the URL after click() returns.
    window.setTimeout(() => URL.revokeObjectURL(downloadUrl), DOWNLOAD_URL_LIFETIME_MS);
  }
}
